import React, { useState, useEffect } from "react";
import useApi from "../../hooks/useApi";

const ProjectImageUpload = ({onFilesUploaded}) => {
    const [selectedFiles, setSelectedFiles] = useState(undefined);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [message, setMessage] = useState([]);
    const [imageKey, setImageKey] = useState(Date.now());
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const blobStorageUploadFormFiles = useApi('blobstorage/UploadFormFilesBlobAsync');

    useEffect(() => {
        if (!blobStorageUploadFormFiles.loading && !blobStorageUploadFormFiles.error) {
            const uploaded = blobStorageUploadFormFiles.data;
            if (uploaded && uploaded.length) {
                setSelectedFiles(undefined);
                setUploadComplete(uploaded);
                setUploading(false);
            }
        } else {
            setUploadError("Couldn't upload project files, please try again");
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [blobStorageUploadFormFiles.data]);

    const selectFiles = (event) => {
        let images = [];

        for (let i = 0; i < event.target.files.length; i++) {
            images.push(URL.createObjectURL(event.target.files[i]));
        }

        setSelectedFiles(event.target.files);
        setImagePreviews(images);
        setUploadError('');
        setUploadedFiles([]);
        setMessage([]);
        uploadSelectedFiles(event.target.files);
    };

    const uploadFiles = async (files) => {
        const formData = new FormData();

        for (const file of files) {
            formData.append("files", file);
        }

        blobStorageUploadFormFiles.post(formData, {headers: {'Accept': 'multipart/form-data'}});
    }

    const uploadSelectedFiles = (filesSelected) => {
        const files = Array.from(filesSelected);

        if (files.length > 0) {
            setUploading(true);
            uploadFiles(files);
        }
    }

    const uploadImages = () => {
        const files = Array.from(selectedFiles);

        if (files.length > 0) {
            setUploading(true);
            uploadFiles(files);
        }
    }

    const clearImages = () => {
        setSelectedFiles(undefined);
        setImagePreviews([]);
        setUploadComplete([]);
        setImageKey(Date.now());
    };

    const handlePreviewClick = (e) => {
        let key = e.target.dataset.key;
        if (uploadedFiles[key] !== undefined) {
            window.open(uploadedFiles[key], '_blank')
        }
    }

    const setUploadComplete = (uploaded) => {
        setUploadedFiles(uploaded);
        if (typeof onFilesUploaded === 'function') {
            onFilesUploaded(getUploadData(uploaded));
        }
    }

    const getUploadData = (uploaded) => {
        let uploadData = [];

        if (uploaded) {
            uploaded.forEach(url => {
                let fileName = new URL(url).searchParams.get('fileName');

                if (fileName) {
                    uploadData.push({
                        fileName: fileName,
                        description: ''
                    });
                }
            });
        }

        return uploadData;
    } 

    return (
        <div>
            <div className="row">
                <div className="col-sm-3">
                    <label className="btn btn-default p-0">
                        <input
                            className="w-100"
                            type="file"
                            multiple
                            accept="image/*"
                            key={imageKey}
                            onChange={selectFiles}
                        />
                    </label>
                </div>

                <div className="col-sm-3">
                    <button
                        className="btn btn-success btn-sm col-4"
                        disabled={!selectedFiles}
                        onClick={uploadImages}
                        hidden={true}
                    >
                        Upload
                    </button>
                    <div className={uploading ? "upload-spinner d-inline ms-1" : "visually-hidden"}>
                        <div role="status" className="spinner-border spinner-border-sm text-success align-middle">
                            <span className="sr-only"></span>
                        </div>
                    </div>
                    <div className={uploadedFiles.length > 0 ? "text-success d-inline" : "visually-hidden"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" className="bi bi-check" viewBox="0 0 16 16">
                            <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                        </svg>
                    </div>
                    <div className={uploadError !== '' ? "text-danger d-inline ms-1" : "visually-hidden"}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-check" viewBox="0 0 16 16">
                            <path d="M8.982 1.566a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566zM8 5c.535 0 .954.462.9.995l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 5.995A.905.905 0 0 1 8 5zm.002 6a1 1 0 1 1 0 2 1 1 0 0 1 0-2z" />
                        </svg>
                    </div>
                    <div className={!uploading && uploadedFiles.length > 0 && uploadError === '' ? "d-inline-block ms-2 col-4" : "visually-hidden"}>
                        <button
                            className="btn btn-danger btn-sm w-100"
                            disabled={uploadedFiles.length === 0}
                            onClick={clearImages}
                        >
                            Remove all
                        </button>
                    </div>
                </div>
                
            </div>

            {imagePreviews && (
                <div>
                    {imagePreviews.map((img, i) => {
                        return (
                            <img className="preview img-thumbnail position-relative mt-2" key={i} data-key={i} style={{ maxWidth: '200px' }} src={img} alt={"image-" + i} onClick={handlePreviewClick} role={uploadedFiles[i] !== undefined ? 'button' : null} />
                        );
                    })}
                </div>
            )}

            {message.length > 0 && (
                <div className="alert alert-secondary mt-2" role="alert">
                    <ul>
                        {message.map((item, i) => {
                            return <li key={i}>{item}</li>;
                        })}
                    </ul>
                </div>
            )}

        </div>
    );
};

export default ProjectImageUpload;
